<script setup>
import { ref, onMounted } from 'vue'
import {
  listCards,
  createCard,
  updateCollection,
  addToGroup,
  updateGroupEntry,
  deleteGroupEntry,
  getMe,
} from '@/api/cardApi'

const cards = ref([])
const loading = ref(false)
const errorMessage = ref('')
const search = ref('')

// Database managers (is_staff) can add to the shared catalog; everyone
// else is a collection manager -- catalog-write UI stays hidden for them
// (the backend enforces this too, CardViewSet.get_permissions -- this is
// just so they never see a form that would 403).
const isDatabaseManager = ref(false)

const showAddForm = ref(false)
const newCard = ref({
  card_id: '',
  card_name: '',
  set_id: '',
  set_type: 'OP',
  language: 'JP',
  rarity: '',
  card_color: '',
})

// A single default group every user gets on first use -- keeps the UI
// simple (a plain quantity field per card) while still going through the
// real per-user Group/CardGroupEntry model underneath. Naming a second
// binder/deck is still possible via the API, just not exposed in this
// first pass of the UI (see plan's frontend scope note).
const DEFAULT_GROUP_NAME = 'My Collection'

async function fetchCards() {
  loading.value = true
  errorMessage.value = ''
  try {
    const params = {}
    if (search.value) params.search = search.value
    const data = await listCards(params)
    cards.value = data.results
  } catch (err) {
    errorMessage.value = err.message
  } finally {
    loading.value = false
  }
}

function marketPrice(card) {
  const price = card.prices?.find((p) => p.source === 'optcg_market' && p.price != null)
    || card.prices?.find((p) => p.source === 'cardtrader_en' && p.price != null)
  return price ? `€${price.price}` : null
}

async function toggleFavourite(card) {
  const current = card.collection?.favourite || false
  card.collection = await updateCollection(card.id, { favourite: !current })
}

async function toggleWishlist(card) {
  const current = card.collection?.to_buy || false
  card.collection = await updateCollection(card.id, { to_buy: !current })
}

async function setQuantity(card, rawValue) {
  const qty = Number(rawValue)
  if (!Number.isFinite(qty) || qty < 0) return

  const existingEntry = card.group_entries?.[0]
  if (existingEntry) {
    if (qty === 0) {
      await deleteGroupEntry(card.id, existingEntry.id)
      card.group_entries = []
    } else {
      card.group_entries = [await updateGroupEntry(card.id, existingEntry.id, { quantity: qty })]
    }
  } else if (qty > 0) {
    card.group_entries = [await addToGroup(card.id, { group_name: DEFAULT_GROUP_NAME, quantity: qty })]
  }
}

async function submitNewCard() {
  errorMessage.value = ''
  try {
    const created = await createCard(newCard.value)
    cards.value = [created, ...cards.value]
    showAddForm.value = false
    newCard.value = { card_id: '', card_name: '', set_id: '', set_type: 'OP', language: 'JP', rarity: '', card_color: '' }
  } catch (err) {
    errorMessage.value = err.message
  }
}

onMounted(async () => {
  fetchCards()
  try {
    const me = await getMe()
    isDatabaseManager.value = me.is_staff
  } catch {
    isDatabaseManager.value = false
  }
})
</script>

<template>
  <main class="card-list">
    <div class="toolbar">
      <input v-model="search" type="search" placeholder="Search cards..." @keyup.enter="fetchCards" />
      <button type="button" @click="fetchCards">Search</button>
      <button v-if="isDatabaseManager" type="button" @click="showAddForm = !showAddForm">
        {{ showAddForm ? 'Cancel' : 'Add card' }}
      </button>
    </div>

    <form v-if="isDatabaseManager && showAddForm" class="add-card-form" @submit.prevent="submitNewCard">
      <input v-model="newCard.card_id" placeholder="Card ID (e.g. OP01-001)" required />
      <input v-model="newCard.card_name" placeholder="Card name" required />
      <input v-model="newCard.set_id" placeholder="Set ID (e.g. OP01)" />
      <select v-model="newCard.set_type">
        <option value="OP">OP Booster</option>
        <option value="ST">Starter Deck</option>
        <option value="EB">Extra Booster</option>
        <option value="PRB">Premium Booster</option>
        <option value="P">Promo</option>
      </select>
      <select v-model="newCard.language">
        <option value="JP">Japanese</option>
        <option value="EN">English</option>
      </select>
      <input v-model="newCard.rarity" placeholder="Rarity (e.g. SR)" />
      <button type="submit">Add to catalog</button>
    </form>

    <p v-if="errorMessage" class="error">{{ errorMessage }}</p>
    <p v-if="loading">Loading...</p>

    <div class="grid">
      <article v-for="card in cards" :key="card.id" class="card-tile">
        <img v-if="card.card_image" :src="card.card_image" :alt="card.card_name" />
        <h3>{{ card.card_name }}</h3>
        <p class="card-id">{{ card.card_id }} · {{ card.rarity }} · {{ card.language }}</p>
        <p v-if="marketPrice(card)" class="price">{{ marketPrice(card) }}</p>

        <div class="actions">
          <label>
            <input type="checkbox" :checked="card.collection?.favourite" @change="toggleFavourite(card)" />
            Favourite
          </label>
          <label>
            <input type="checkbox" :checked="card.collection?.to_buy" @change="toggleWishlist(card)" />
            Wishlist
          </label>
          <label>
            Qty
            <input
              type="number"
              min="0"
              :value="card.group_entries?.[0]?.quantity || 0"
              @change="setQuantity(card, $event.target.value)"
            />
          </label>
        </div>
      </article>
    </div>
  </main>
</template>

<style scoped>
.toolbar { display: flex; gap: 0.5rem; padding: 1rem; }
.add-card-form { display: flex; flex-wrap: wrap; gap: 0.5rem; padding: 0 1rem 1rem; }
.grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 1rem; padding: 1rem; }
.card-tile { border: 1px solid #ddd; border-radius: 8px; padding: 0.5rem; }
.card-tile img { width: 100%; border-radius: 4px; }
.card-id { font-size: 0.8rem; color: #666; }
.actions { display: flex; flex-direction: column; gap: 0.25rem; margin-top: 0.5rem; font-size: 0.85rem; }
.error { color: #b00020; padding: 0 1rem; }
</style>
